import React, { useState, useEffect, useRef } from 'react';
import {
  Row, Col, Card, Button, Typography, notification, Modal,
} from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { namespaces } from '../../i18n/i18n.constants';
import {
  del, get, RequestWrapper, ResponseWrapper, isSuperAgentError,
} from '../../dataSource/requests';
import { Get_MessageInfo_ListAll_MessageInfoOutput } from '../../sharedTypes/MessageInfoTypes';
import { getUserId, removeUserFromStorage } from '../../utils/users';
import './DeleteAccount.scss';

const DeleteAccount = (): JSX.Element => {
  const navigate = useNavigate();
  const userID: string | null = getUserId();
  const { t } = useTranslation('namespaces');

  const [currentPage, setCurrentPage] = useState<number>(0);
  const [
    deleteMessagesArray,
    setDeleteMessagesArray,
  ] = useState<Array<Get_MessageInfo_ListAll_MessageInfoOutput>>([]);
  const [emailRegistered, setEmailRegistered] = useState<string>('');

  const getMessageInfoListAllRequest = useRef<RequestWrapper|undefined>(undefined);
  const deleteUserRequest = useRef<RequestWrapper|undefined>(undefined);

  useEffect(() => {
    const getMessages = async (): Promise<void> => {
      let response: ResponseWrapper | undefined;
      let responseError: ResponseWrapper | undefined;
      let genericError: Error | undefined;

      const requestReference: RequestWrapper = get({
        url: '/message-info/listAll',

        frontConfigs: {
          navigateFunction: navigate,
        },
      });

      getMessageInfoListAllRequest.current = requestReference;

      try {
        response = await requestReference;
      } catch (error: unknown) {
        if (isSuperAgentError(error)) {
          responseError = error.response;
        } else {
          genericError = error as Error;
        }
      }

      switch (true) {
        case (response?.body?.code === 21000):
          // "All messages listed successfully", 21000
          setDeleteMessagesArray(response?.body?.messageInfoOutput);
          setEmailRegistered(response?.body?.exclusionEmailExists);
          break;

        case (responseError?.body?.code === -4001):
          // "You must be authenticated to perform this operation", -4001
          break;

        case (responseError?.body?.code === -4003):
          // "You do not have permission to perform this operation", -4003
          break;

        case (genericError?.message?.includes('has been aborted')):
          // dont show anything when request is aborted
          break;

        default:
          notification.error({
            message: t('error', { ns: namespaces.common }),
            description: t('default_error_listmessages', { ns: namespaces.pages.deleteAccount.deleteAccount }),
          });
          break;
      }
    };

    getMessages();

    return () => {
      if (getMessageInfoListAllRequest.current) {
        getMessageInfoListAllRequest.current.abort();
      }

      if (deleteUserRequest.current) {
        deleteUserRequest.current.abort();
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const deleteUser = async (): Promise<void> => {
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    if (!userID) {
      notification.error({
        message: t('error', { ns: namespaces.common }),
        description: t('user_notfound', { ns: namespaces.pages.deleteAccount.deleteAccount }),
      });

      navigate('/');
      return;
    }

    const requestReference: RequestWrapper = del({
      url: `/user/requestUserDeletion/${userID}`,

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    deleteUserRequest.current = requestReference;

    try {
      response = await requestReference;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response?.body?.code === 1022):
        // "Delete user successfully", 1022
        Modal.confirm({
          title: 'Atenção',
          content: 'Sua solicitação foi processada e você terá um retorno no prazo de 15 dias.',
          okText: 'Ok',
          okCancel: false,
          onOk: () => {
            removeUserFromStorage();
            navigate('/');
          },
          autoFocusButton: null,
          icon: (<WarningOutlined />),
          width: 500,
        });

        break;

      case (responseError?.body?.code === -1030):
        // "Different id of the logged in user", -1030
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: t('different_user_id', { ns: namespaces.pages.deleteAccount.deleteAccount }),
        });
        break;

      case (responseError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (responseError?.body?.code === -4003):
        // "You do not have permission to perform this operation", -4003
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: t('defaultError', { ns: namespaces.common }),
        });
        break;
    }
  };

  const validEmail = (): void => {
    if (emailRegistered) {
      if (currentPage === 0) {
        setCurrentPage(1);
        return;
      }

      deleteUser();
    } else {
      Modal.confirm({
        title: 'Atualmente, nenhum e-mail para verificar a sua solicitação de exclusão de conta está cadastrado pelo administrador no sistema',
        content: 'Por favor, contate a Coopercitrus e informe esse problema para que ele possa ser resolvido.',
        okText: 'Ok',
        okCancel: false,
        autoFocusButton: null,
        icon: (<WarningOutlined />),
        width: 800,
      });
    }
  };

  return (
    <div className="DeleteAccount">
      <Row
        gutter={[24, 24]}
      >
        <Col
          xs={{ span: 24 }}
        >
          <Row
            gutter={[24, 24]}
            justify="space-between"
          >
            <Col
              xs={{ span: 24 }}
              lg={{ span: 10 }}
            >
              <Typography.Title
                className="DeleteAccount__PageTitle"
              >
                {t('delete_account_title', { ns: namespaces.pages.deleteAccount.deleteAccount })}
              </Typography.Title>
            </Col>
          </Row>

          <Row>
            <Col
              xs={{ span: 24 }}
            >
              <Card>
                <Row>
                  <Col>
                    {
                      deleteMessagesArray.length
                        ? deleteMessagesArray
                          .filter(
                            (_element, elementIndex) => elementIndex === currentPage,
                          )
                          .map((element) => {
                            const paragraphs = element.description.split('\n\n');

                            return (
                              <>
                                {
                                  paragraphs.map((paragraphElement) => {
                                    return (
                                      <Typography.Paragraph>
                                        {paragraphElement}
                                      </Typography.Paragraph>
                                    );
                                  })
                                }
                              </>
                            );
                          })
                        : null
                    }
                  </Col>
                </Row>

                <Row
                  className="DeleteAccount__PrimaryButtonRow"
                  justify="center"
                  gutter={[24, 24]}
                >
                  <Col
                    xs={{ span: 24 }}
                    lg={{ span: 12 }}
                  >
                    <Button
                      type="primary"
                      htmlType="button"
                      block
                      onClick={():void => validEmail()}
                    >
                      {
                        (currentPage === 0)
                          ? t('continue_button_text', { ns: namespaces.pages.deleteAccount.deleteAccount })
                          : t('delete_account_button_text', { ns: namespaces.pages.deleteAccount.deleteAccount })
                      }
                    </Button>
                  </Col>
                </Row>

                <Row
                  className="DeleteAccount__CancelButtonRow"
                  justify="center"
                  gutter={[24, 24]}
                >
                  <Col
                    xs={{ span: 24 }}
                    lg={{ span: 12 }}
                  >
                    <Button
                      type="default"
                      htmlType="button"
                      block
                      onClick={
                        (): void => {
                          navigate('/home');
                        }
                      }
                    >
                      {t('cancel_button_text', { ns: namespaces.pages.deleteAccount.deleteAccount })}
                    </Button>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>
    </div>
  );
};

DeleteAccount.propTypes = {};
DeleteAccount.defaultProps = {};

export default DeleteAccount;
